import { useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools } from '@fortawesome/free-solid-svg-icons';
import MainNavigation from './MainNavigation';
import AdminDrawer from './AdminDrawer';
import { Button } from './';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { CircuitBreakerDashboard } from './CircuitBreakerStatus';
import { checkIsMaster } from '../utils/config-calls';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const { preloadLoginPage, t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminDrawerOpen, setIsAdminDrawerOpen] = useState(false);

  // Vérifier le statut admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const result = await checkIsMaster();
        setIsAdmin(result.success && result.isMaster);
        debug.log(config, 'Statut admin vérifié:', result.success && result.isMaster);
      } catch (error) {
        debug.error(config, 'Erreur lors de la vérification du statut admin:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);
  
  // Fonction de déconnexion améliorée qui précharge les traductions
  const handleLogout = async () => {
    debug.log(config, 'Déconnexion initiée, préchargement des traductions LOGIN');
    
    try {
      // Précharge les traductions de la page de login avant la déconnexion
      await preloadLoginPage();
      debug.log(config, 'Traductions LOGIN préchargées, déconnexion...');
      
      // Effectuer la déconnexion
      logout();
    } catch (error) {
      debug.error(config, 'Erreur lors du préchargement des traductions pour la déconnexion:', error);
      // Continuer avec la déconnexion même si le préchargement échoue
      logout();
    }
  };
  
  return (
    <div className="min-h-screen max-w-7xl mx-auto">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="WA Bellow" className="w-8 h-8" />
          </div>
          
          {/* User menu */}
          <div className="flex items-center gap-4">
            {/* Circuit Breaker Status and Admin Button */}
            <div className="flex items-center gap-2">
              <CircuitBreakerDashboard
                  container="none" 
                  variant="standard"
                  size="sm"
                  />
              
              {/* Admin Tools Button - visible only for admins */}
              {isAdmin && (
                <button
                  className="circuit-breaker-status circuit-breaker--sm wa-admin-button"
                  onClick={() => setIsAdminDrawerOpen(true)}
                  title={t('NAVIGATION', 'ADMIN_TOOLS', 'Outils d\'Administration')}
                  aria-label="Ouvrir les outils d'administration"
                >
                  <FontAwesomeIcon icon={faTools} />
                </button>
              )}
            </div>
            
            <div className="flex items-center">
              <span className="mr-4 text-sm text-gray-700">
                {user?.username}
              </span>
              <Button
                variant="danger"
                size="sm"
                onClick={handleLogout}
              >
                {t('NAVIGATION', 'LOGOUT', 'Déconnexion')}
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Navigation principale */}
      <div className="pt-2.5 pb-0 mb-0">
        <MainNavigation />
      </div>
      
      {/* Admin Drawer */}
      <AdminDrawer 
        isOpen={isAdminDrawerOpen}
        onClose={() => setIsAdminDrawerOpen(false)}
      />
      
      {/* Main content */}
      <div className="py-0">
        <main className="page-container">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
